import React, { useEffect, useMemo, useState } from 'react';
import '../pages/ManageSMRTCOP.css';
import './SubscriptionPanel.css';
import ModuleEditModal, { ModuleStatus } from './ModuleEditModal';
import NewModuleWizard from './NewModuleWizard';
import DeleteConfirmModal from './DeleteConfirmModal';
import { ModuleDoc, useModules } from '../context/ModulesContext';

interface SubscriptionData {
  orgID: string;
  planFeatures: Record<string, { enabled: boolean }>;
  overrideFeatures: Record<string, { enabled: boolean }>;
}

interface ModulesPanelProps {
  subscriptions: SubscriptionData[];
  // Rendered inside a tab: always open, no collapse caret.
  embedded?: boolean;
}

interface ModuleLocalOverride {
  description?: string;
  status?: ModuleStatus;
}

function isEffectivelyEnabled(key: string, sub: SubscriptionData): boolean {
  const override = sub.overrideFeatures?.[key];
  if (override !== undefined) return override.enabled;
  return sub.planFeatures?.[key]?.enabled === true;
}

export default function ModulesPanel({
  subscriptions,
  embedded = false,
}: ModulesPanelProps) {
  const { refresh: refreshModulesContext } = useModules();
  const [expanded, setExpanded] = useState(false);
  const isOpen = embedded || expanded;
  const [catalog, setCatalog] = useState<ModuleDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [localOverrides, setLocalOverrides] = useState<Record<string, ModuleLocalOverride>>({});
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [pendingDeleteKey, setPendingDeleteKey] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/modules`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Failed to fetch modules');
        setCatalog(await res.json());
      } catch (err) {
        console.error('Failed to load module catalog:', err);
        setCatalog([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const inUseMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const mod of catalog) {
      map[mod.key] = subscriptions.some((sub) => isEffectivelyEnabled(mod.key, sub));
    }
    return map;
  }, [catalog, subscriptions]);

  const getDescription = (mod: ModuleDoc): string =>
    localOverrides[mod.key]?.description ?? mod.description ?? '—';

  const getStatus = (mod: ModuleDoc): ModuleStatus =>
    localOverrides[mod.key]?.status ?? (mod.status as ModuleStatus);

  const handleSave = async (key: string, description: string, status: ModuleStatus) => {
    const mod = catalog.find((m) => m.key === key);
    if (!mod) return;
    const statusChanged = mod.status !== status;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/modules/${encodeURIComponent(mod.id)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description, status }),
      });
      if (!res.ok) throw new Error('Failed to update module');
      const updated = await res.json();
      setCatalog((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
      setLocalOverrides((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      if (statusChanged) refreshModulesContext();
    } catch (err) {
      console.error('Failed to persist module edit:', err);
      alert('Failed to save module changes.');
    }
  };

  const handleDelete = async (key: string) => {
    const mod = catalog.find((m) => m.key === key);
    if (!mod) return;
    if (mod.status === 'active') {
      alert('Cannot delete an active module. Set it to inactive first.');
      return;
    }
    setDeleting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/modules/${encodeURIComponent(mod.id)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || 'Failed to delete module');
      }
      setCatalog((prev) => prev.filter((m) => m.id !== mod.id));
      setLocalOverrides((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setPendingDeleteKey(null);
    } catch (err: any) {
      console.error('Failed to delete module:', err);
      alert(err?.message || 'Failed to delete module.');
    } finally {
      setDeleting(false);
    }
  };

  const editingModule = editingKey
    ? (() => {
        const mod = catalog.find((m) => m.key === editingKey);
        if (!mod) return null;
        return {
          key: mod.key,
          name: mod.name,
          description: getDescription(mod),
          status: getStatus(mod),
          inUse: inUseMap[mod.key] ?? false,
        };
      })()
    : null;

  return (
    <div style={{ marginBottom: 24 }}>
      {embedded
        ? isOpen && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginBottom: 8,
              }}
            >
              <button
                className="smrtcop-btn-primary"
                title="Add Module"
                onClick={() => setWizardOpen(true)}
              >
                + Add Module
              </button>
            </div>
          )
        : (
          <div
            style={{
              position: 'sticky',
              top: '5rem',
              zIndex: 10,
              background: '#1a1a1a',
              padding: '10px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderRadius: 6,
              color: '#9aa0a6',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              fontSize: '0.8rem',
              fontWeight: 600,
            }}
          >
            <div
              onClick={() => setExpanded((v) => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
            >
              <span style={{ fontSize: '0.85rem' }}>{expanded ? '▼' : '▶'}</span>
              <h2 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600, color: '#cbd5e1', textTransform: 'none', letterSpacing: 'normal' }}>Modules</h2>
            </div>
            {isOpen && (
              <button
                className="smrtcop-btn-primary"
                title="Add Module"
                onClick={() => setWizardOpen(true)}
              >
                + Add Module
              </button>
            )}
          </div>
        )}

      {isOpen && (
        <div style={{ background: '#1e1e1e', borderRadius: 8, padding: 12, marginTop: 8 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1.2fr 2fr 120px 120px',
              gap: 12,
              padding: '8px 12px',
              background: '#1a1a1a',
              borderRadius: 6,
              fontSize: '0.8rem',
              fontWeight: 600,
              color: '#9aa0a6',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              marginBottom: 4,
              position: embedded ? 'sticky' : undefined,
              top: embedded ? 0 : undefined,
              zIndex: embedded ? 5 : undefined,
              boxShadow: embedded ? '0 2px 6px rgba(0,0,0,0.5)' : undefined,
            }}
          >
            <span>Name</span>
            <span>Description</span>
            <span>Status</span>
            <span style={{ textAlign: 'center' }}>Actions</span>
          </div>

          {loading ? (
            <div style={{ padding: '16px 12px', color: '#9aa0a6', fontSize: '0.9rem', textAlign: 'center' }}>
              Loading modules...
            </div>
          ) : catalog.length === 0 ? (
            <div style={{ padding: '16px 12px', color: '#9aa0a6', fontSize: '0.9rem', textAlign: 'center' }}>
              No modules found.
            </div>
          ) : (
            catalog.map((mod) => {
              const status = getStatus(mod);
              const isActive = status === 'active';
              return (
                <div
                  key={mod.key}
                  className="smrtcop-overview-row"
                  style={{
                    gridTemplateColumns: '1.2fr 2fr 120px 120px',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{mod.name}</span>
                  <span style={{ color: '#cbd5e1' }}>{getDescription(mod)}</span>
                  <span
                    style={{
                      background: isActive ? 'rgba(76, 175, 80, 0.2)' : 'rgba(229, 57, 53, 0.2)',
                      color: isActive ? '#81c784' : '#ef9a9a',
                      padding: '3px 10px',
                      borderRadius: 10,
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: '0.03em',
                      width: 'fit-content',
                    }}
                  >
                    {isActive ? 'Active' : 'Inactive'}
                  </span>
                  <div className="smrtcop-actions">
                    <button
                      className="smrtcop-btn-icon smrtcop-btn-edit"
                      onClick={() => setEditingKey(mod.key)}
                      title="Edit module"
                    >
                      ✎
                    </button>
                    {mod.status === 'inactive' && (
                      <button
                        className="smrtcop-btn-icon smrtcop-btn-delete"
                        onClick={() => setPendingDeleteKey(mod.key)}
                        title="Delete Module"
                      >
                        🗑
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      <ModuleEditModal
        isOpen={!!editingKey}
        onClose={() => setEditingKey(null)}
        onSave={(desc, status) => editingKey && handleSave(editingKey, desc, status)}
        module={editingModule}
      />

      <DeleteConfirmModal
        isOpen={!!pendingDeleteKey}
        onClose={() => !deleting && setPendingDeleteKey(null)}
        onConfirm={() => pendingDeleteKey && handleDelete(pendingDeleteKey)}
        title="Delete Module"
        message="Are you sure you want to delete this module?"
        confirmLabel={deleting ? 'Deleting...' : 'Delete'}
        isLoading={deleting}
      />

      <NewModuleWizard
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={(mod) =>
          setCatalog((prev) => [...prev, mod].sort((a, b) => a.displayOrder - b.displayOrder))
        }
      />
    </div>
  );
}
